import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useVenue } from "../VenueProvider";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ACCENT, MONTHS_ES, MONTHS_SHORT, formatMXN, CustomTooltip } from "../cuentas/shared";
import { formatCalendarDate } from "../../utils/dates";
import { estacionalidadMensual, estacionalidadPorAnio } from "../../utils/reportes";
import { Kpi } from "./shared";

// Un color por año en la gráfica; el más reciente lleva el verde de marca.
const SERIE_COLORES = [ACCENT, "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899"];

// Qué meses del año son mejor negocio. La utilidad se atribuye al mes en
// que OCURRE el evento, no al mes en que se cobró — las reservas de abril
// pueden empezar a pagar desde junio del año anterior, y eso es caja (vive
// en el Flujo de caja), no estacionalidad. Solo cuenta eventos ya
// ocurridos, y cada año se lee POR SEPARADO: la gráfica compara agosto
// contra agosto y cada año tiene su propia tabla.
const ReporteEstacionalidad = () => {
  const navigate = useNavigate();
  const { events, costos, calendarTimezone: tz } = useVenue();

  // Acumulado de toda la historia: solo para los KPIs de arriba.
  const acumulado = useMemo(
    () => estacionalidadMensual({ events, costos, timeZone: tz }),
    [events, costos, tz]
  );
  const porAnio = useMemo(
    () => estacionalidadPorAnio({ events, costos, timeZone: tz }),
    [events, costos, tz]
  );

  const conDatos = acumulado.filter((m) => m.eventos > 0);
  const mejor = conDatos.reduce((a, b) => (b.utilidad > (a?.utilidad ?? -Infinity) ? b : a), null);
  const flojo = conDatos.reduce((a, b) => (b.utilidad < (a?.utilidad ?? Infinity) ? b : a), null);
  const totalEventos = conDatos.reduce((s, m) => s + m.eventos, 0);
  const totalUtilidad = conDatos.reduce((s, m) => s + m.utilidad, 0);

  const [mesAbierto, setMesAbierto] = useState(null); // `${anio}-${mes}`

  // Gráfica: una barra por año en cada mes, en orden cronológico.
  const aniosAsc = useMemo(
    () => [...porAnio].sort((a, b) => a.anio - b.anio),
    [porAnio]
  );
  const chartData = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const fila = { label: MONTHS_SHORT[i] };
        aniosAsc.forEach((a) => {
          fila[String(a.anio)] = Math.round(a.meses[i].utilidad);
        });
        return fila;
      }),
    [aniosAsc]
  );

  if (totalEventos === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-10 text-center text-sm text-zinc-400 shadow-sm">
        Todavía no hay eventos pasados para leer la estacionalidad.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Mes de mayor utilidad"
          value={mejor ? MONTHS_ES[mejor.mes] : "—"}
          tone="text-emerald-600"
          hint={mejor ? `${formatMXN(mejor.utilidad)} acumulados` : null}
        />
        <Kpi
          label="Mes de menor utilidad"
          value={flojo ? MONTHS_ES[flojo.mes] : "—"}
          tone={flojo && flojo.utilidad < 0 ? "text-red-600" : ""}
          hint={flojo ? `${formatMXN(flojo.utilidad)} acumulados` : null}
        />
        <Kpi
          label="Eventos analizados"
          value={totalEventos}
          hint="Solo eventos que ya ocurrieron"
        />
        <Kpi
          label="Utilidad promedio"
          value={formatMXN(totalEventos > 0 ? totalUtilidad / totalEventos : 0)}
          hint="Por evento, toda la historia"
        />
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
              {aniosAsc.map((a, idx) => (
                <Bar
                  key={a.anio}
                  dataKey={String(a.anio)}
                  name={String(a.anio)}
                  fill={SERIE_COLORES[(aniosAsc.length - 1 - idx) % SERIE_COLORES.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs text-zinc-400">
          Utilidad de los eventos de cada mes, un color por año.
        </p>
      </div>

      {/* Una tabla por año, del más reciente al más viejo */}
      {porAnio.map(({ anio, meses }) => {
        const conEventos = meses.filter((m) => m.eventos > 0);
        if (conEventos.length === 0) return null;
        const totalAnio = conEventos.reduce(
          (acc, m) => ({
            eventos: acc.eventos + m.eventos,
            valor: acc.valor + m.valor,
            utilidad: acc.utilidad + m.utilidad,
          }),
          { eventos: 0, valor: 0, utilidad: 0 }
        );
        return (
          <div
            key={anio}
            className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-700/40 flex items-baseline gap-3">
              <div className="text-sm font-semibold text-zinc-900 dark:text-white">{anio}</div>
              <div className="text-xs text-zinc-500">
                {totalAnio.eventos} {totalAnio.eventos === 1 ? "evento" : "eventos"} ·{" "}
                {formatMXN(totalAnio.utilidad)} de utilidad
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Mes</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Eventos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Valor</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Costos de sus eventos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Utilidad</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Utilidad por evento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {conEventos.map((m) => {
                    const key = `${anio}-${m.mes}`;
                    const abierto = mesAbierto === key;
                    const costosMes = m.valor - m.utilidad;
                    return (
                      <React.Fragment key={key}>
                        <tr
                          onClick={() => setMesAbierto(abierto ? null : key)}
                          className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition"
                          title="Ver los eventos de este mes"
                        >
                          <td className="px-4 py-2 text-zinc-900 dark:text-white font-medium whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <ChevronRightIcon
                                className={`h-4 w-4 text-zinc-400 transition-transform ${abierto ? "rotate-90" : ""}`}
                              />
                              {MONTHS_ES[m.mes]}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-zinc-500">{m.eventos}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                            {formatMXN(m.valor)}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {costosMes !== 0 ? (
                              <span className="text-red-600">{formatMXN(costosMes)}</span>
                            ) : (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <span className={`font-medium ${m.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}>
                              {formatMXN(m.utilidad)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap text-zinc-500">
                            {formatMXN(m.promedio)}
                          </td>
                        </tr>
                        {abierto && (
                          <tr>
                            <td colSpan={6} className="px-5 py-4 bg-zinc-50/70 dark:bg-zinc-800/30">
                              <div className="space-y-1">
                                <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                                  <span className="w-24 shrink-0">Fecha</span>
                                  <span>Evento</span>
                                  <span className="ml-auto w-24 text-right">Ingresos</span>
                                  <span className="w-24 text-right">Costos</span>
                                  <span className="w-24 text-right">Utilidad</span>
                                </div>
                                {m.lista.map((e) => {
                                  const costosEv = e.valor - e.utilidad;
                                  return (
                                    <div key={e.id} className="flex items-baseline gap-2 text-xs">
                                      <span className="text-zinc-400 w-24 shrink-0">
                                        {formatCalendarDate(e.startTime, tz, { style: "short", weekday: "short", instante: true })}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                                        className="truncate hover:underline text-left"
                                        style={{ color: ACCENT }}
                                      >
                                        {e.nombre}
                                      </button>
                                      <span className="ml-auto w-24 text-right text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                                        {formatMXN(e.valor)}
                                      </span>
                                      <span className="w-24 text-right whitespace-nowrap">
                                        {costosEv !== 0 ? (
                                          <span className="text-red-600">{formatMXN(costosEv)}</span>
                                        ) : (
                                          <span className="text-zinc-300 dark:text-zinc-600">—</span>
                                        )}
                                      </span>
                                      <span
                                        className={`w-24 text-right font-medium whitespace-nowrap ${
                                          e.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                                        }`}
                                      >
                                        {formatMXN(e.utilidad)}
                                      </span>
                                    </div>
                                  );
                                })}
                                <div className="flex items-baseline gap-2 text-xs pt-1 border-t border-zinc-200/60 dark:border-zinc-700/40 font-semibold">
                                  <span className="w-24 shrink-0" />
                                  <span className="text-zinc-500">
                                    Total · {m.eventos} {m.eventos === 1 ? "evento" : "eventos"}
                                  </span>
                                  <span className="ml-auto w-24 text-right text-zinc-900 dark:text-white whitespace-nowrap">
                                    {formatMXN(m.valor)}
                                  </span>
                                  <span className="w-24 text-right text-red-600 whitespace-nowrap">
                                    {costosMes !== 0 ? formatMXN(costosMes) : "—"}
                                  </span>
                                  <span
                                    className={`w-24 text-right whitespace-nowrap ${
                                      m.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                                    }`}
                                  >
                                    {formatMXN(m.utilidad)}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700/60">
                  <tr>
                    <td className="px-4 py-2.5 font-semibold text-zinc-900 dark:text-white">Total {anio}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white">{totalAnio.eventos}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white whitespace-nowrap">{formatMXN(totalAnio.valor)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">
                      {totalAnio.valor - totalAnio.utilidad !== 0
                        ? formatMXN(totalAnio.valor - totalAnio.utilidad)
                        : "—"}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${totalAnio.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {formatMXN(totalAnio.utilidad)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-zinc-500 whitespace-nowrap">
                      {formatMXN(totalAnio.eventos > 0 ? totalAnio.utilidad / totalAnio.eventos : 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}

      <p className="text-xs text-zinc-400">
        La utilidad se atribuye al mes en que ocurre el evento, sin importar
        cuándo se cobró: los anticipos y liquidaciones viven en el Flujo de
        caja. Solo cuentan eventos que ya ocurrieron. Los KPIs de arriba sí
        acumulan toda la historia; las tablas y la gráfica leen cada año por
        separado.
      </p>
    </div>
  );
};

export default ReporteEstacionalidad;
